'use client'

import { Input } from '@/components/forms/inputs/text/Input'
import { Badge } from '@/components/ui/common/Badge'
import { Button } from '@/components/ui/common/Button'
import { cn } from '@/utils/common'
import {
  BookOpen,
  Calculator,
  ChevronRight,
  Clock,
  Code,
  GraduationCap,
  Heart,
  MapPin,
  Palette,
  Scale,
  Search,
  Star,
  Stethoscope,
  TrendingUp,
  Users
} from 'lucide-react'
import { useState } from 'react'
interface StudyField {
  id: number
  name: string
  category: string
  description: string
  duration: string
  difficulty: 'Łatwy' | 'Średni' | 'Trudny'
  popularity: number
  employmentRate: number
  averageSalary: string
  requiredSubjects: string[]
  universities: string[]
  careerPaths: string[]
  icon: any
  matchPercentage: number
  isFavorite: boolean
}

export default function FieldsOfStudy() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('Wszystkie')
  const [selectedField, setSelectedField] = useState<StudyField | null>(null)

  const studyFields: StudyField[] = [
    {
      id: 1,
      name: 'Informatyka',
      category: 'Techniczne',
      description:
        'Kierunek kształcący specjalistów w dziedzinie technologii informatycznych, programowania, baz danych i systemów komputerowych.',
      duration: '3.5 roku (inż.) + 1.5 roku (mgr)',
      difficulty: 'Trudny',
      popularity: 95,
      employmentRate: 98,
      averageSalary: '8 000 - 15 000 zł',
      requiredSubjects: ['Matematyka (rozszerzona)', 'Informatyka', 'Fizyka'],
      universities: [
        'Politechnika Warszawska',
        'AGH Kraków',
        'Uniwersytet Warszawski',
        'Politechnika Gdańska'
      ],
      careerPaths: [
        'Programista',
        'Analityk systemów',
        'Administrator IT',
        'Data Scientist'
      ],
      icon: Code,
      matchPercentage: 92,
      isFavorite: true
    },
    {
      id: 2,
      name: 'Psychologia',
      category: 'Humanistyczne',
      description:
        'Studia kształcące w zakresie psychologii człowieka, terapii, diagnostyki psychologicznej i badań nad zachowaniem.',
      duration: '5 lat (mgr)',
      difficulty: 'Średni',
      popularity: 88,
      employmentRate: 85,
      averageSalary: '4 500 - 8 000 zł',
      requiredSubjects: ['Biologia', 'Język polski', 'Matematyka'],
      universities: [
        'Uniwersytet Warszawski',
        'Uniwersytet Jagielloński',
        'SWPS',
        'Uniwersytet im. A. Mickiewicza'
      ],
      careerPaths: [
        'Psycholog kliniczny',
        'Psycholog dziecięcy',
        'Terapeuta',
        'HR Business Partner'
      ],
      icon: Stethoscope,
      matchPercentage: 87,
      isFavorite: false
    },
    {
      id: 3,
      name: 'Mechanika i Budowa Maszyn',
      category: 'Techniczne',
      description:
        'Kierunek przygotowujący inżynierów do projektowania, konstruowania i eksploatacji maszyn oraz urządzeń mechanicznych.',
      duration: '3.5 roku (inż.) + 1.5 roku (mgr)',
      difficulty: 'Trudny',
      popularity: 75,
      employmentRate: 94,
      averageSalary: '6 000 - 12 000 zł',
      requiredSubjects: [
        'Matematyka (rozszerzona)',
        'Fizyka (rozszerzona)',
        'Informatyka'
      ],
      universities: [
        'Politechnika Warszawska',
        'Politechnika Krakowska',
        'Politechnika Gdańska',
        'Politechnika Śląska'
      ],
      careerPaths: ['Inżynier mechanik', 'Konstruktor', 'Technolog', 'Manager produkcji'],
      icon: Calculator,
      matchPercentage: 81,
      isFavorite: false
    },
    {
      id: 4,
      name: 'Grafika i Wzornictwo',
      category: 'Artystyczne',
      description:
        'Studia rozwijające kreatywność i umiejętności projektowe w zakresie grafiki, wzornictwa przemysłowego i komunikacji wizualnej.',
      duration: '3 lata (lic.) + 2 lata (mgr)',
      difficulty: 'Średni',
      popularity: 70,
      employmentRate: 78,
      averageSalary: '4 000 - 9 000 zł',
      requiredSubjects: ['Plastyka', 'Informatyka', 'Język angielski'],
      universities: ['ASP Warszawa', 'ASP Kraków', 'SWPS', 'Uniwersytet WSPA'],
      careerPaths: ['Grafik', 'UX/UI Designer', 'Art Director', 'Ilustrator'],
      icon: Palette,
      matchPercentage: 76,
      isFavorite: true
    },
    {
      id: 5,
      name: 'Medycyna',
      category: 'Medyczne',
      description:
        'Prestiżowy kierunek kształcący lekarzy, wymagający doskonałej znajomości nauk ścisłych i dużego zaangażowania.',
      duration: '6 lat (mgr)',
      difficulty: 'Trudny',
      popularity: 98,
      employmentRate: 99,
      averageSalary: '8 000 - 20 000 zł',
      requiredSubjects: ['Biologia (rozszerzona)', 'Chemia (rozszerzona)', 'Matematyka'],
      universities: [
        'Warszawski Uniwersytet Medyczny',
        'Uniwersytet Jagielloński CM',
        'Gdański Uniwersytet Medyczny'
      ],
      careerPaths: ['Lekarz rodzinny', 'Specjalista', 'Chirurg', 'Badacz medyczny'],
      icon: Stethoscope,
      matchPercentage: 89,
      isFavorite: false
    },
    {
      id: 6,
      name: 'Prawo',
      category: 'Społeczne',
      description:
        'Studia prawnicze kształcące prawników, sędziów, prokuratorów i radców prawnych w zakresie prawa polskiego i międzynarodowego.',
      duration: '5 lat (mgr)',
      difficulty: 'Trudny',
      popularity: 85,
      employmentRate: 88,
      averageSalary: '5 000 - 15 000 zł',
      requiredSubjects: ['Język polski', 'Historia', 'WOS'],
      universities: [
        'Uniwersytet Warszawski',
        'Uniwersytet Jagielloński',
        'Uniwersytet im. A. Mickiewicza'
      ],
      careerPaths: ['Adwokat', 'Radca prawny', 'Sędzia', 'Korporacyjny prawnik'],
      icon: Scale,
      matchPercentage: 73,
      isFavorite: false
    }
  ]

  const categories = [
    'Wszystkie',
    'Techniczne',
    'Humanistyczne',
    'Artystyczne',
    'Medyczne',
    'Społeczne'
  ]

  const filteredFields = studyFields.filter((field) => {
    const matchesSearch =
      field.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory =
      selectedCategory === 'Wszystkie' || field.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'Łatwy':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'Średni':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'Trudny':
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  if (selectedField) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-6">
            <button
              className="mb-4 flex items-center text-[#2bcaca] hover:text-[#229999]"
              onClick={() => setSelectedField(null)}
            >
              <ChevronRight className="mr-1 h-4 w-4 rotate-180 transform" />
              Powrót do listy kierunków
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <selectedField.icon className="mr-3 h-8 w-8 text-[#2bcaca]" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {selectedField.name}
                  </h1>
                  <p className="mt-1 text-gray-600">{selectedField.category}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#2bcaca]">
                  {selectedField.matchPercentage}%
                </div>
                <p className="text-sm text-gray-500">dopasowanie</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column */}
            <div className="space-y-6 lg:col-span-2">
              {/* Description */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Opis kierunku</h2>
                <p className="leading-relaxed text-gray-700">
                  {selectedField.description}
                </p>
              </div>

              {/* Required Subjects */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">
                  Wymagane przedmioty maturalne
                </h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {selectedField.requiredSubjects.map((subject, index) => (
                    <div
                      className="border border-[#2bcaca]/20 bg-[#2bcaca]/10 p-3 text-center flex items-center justify-center"
                      key={index}
                    >
                      <span className="font-medium text-[#2bcaca] text-center">{subject}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Universities */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Najlepsze uczelnie</h2>
                <div className="space-y-3">
                  {selectedField.universities.map((uni, index) => (
                    <div
                      className="flex items-center border border-gray-200 p-3 hover:bg-gray-50"
                      key={index}
                    >
                      <GraduationCap className="mr-3 h-5 w-5 text-[#2bcaca]" />
                      <span className="font-medium text-gray-900">{uni}</span>
                      <div className="ml-auto flex items-center">
                        <Star className="mr-1 h-4 w-4 text-yellow-500" />
                        <span className="text-sm text-gray-600">
                          {(4.2 + index * 0.1).toFixed(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Career Paths */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Możliwe ścieżki kariery</h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {selectedField.careerPaths.map((path, index) => (
                    <div
                      className="border border-[#2bcaca]/20 bg-[#2bcaca]/10 p-3"
                      key={index}
                    >
                      <span className="font-medium text-[#2bcaca]">{path}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Kluczowe informacje</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Czas trwania</span>
                    </div>
                    <span className="text-sm font-medium">{selectedField.duration}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <TrendingUp className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Zatrudnialność</span>
                    </div>
                    <span className="font-medium">{selectedField.employmentRate}%</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Popularność</span>
                    </div>
                    <div className="flex items-center">
                      <div className="mr-2 h-2 w-16 rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-[#2bcaca]"
                          style={{ width: `${selectedField.popularity}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium">
                        {selectedField.popularity}%
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Poziom trudności</span>
                    <Badge
                      className={getDifficultyColor(selectedField.difficulty)}
                      variant="outline"
                    >
                      {selectedField.difficulty}
                    </Badge>
                  </div>

                  <div className="border-t pt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Średnie zarobki</span>
                      <span className="font-medium">{selectedField.averageSalary}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Działania</h2>
                <div className="space-y-3">
                  <Button className="w-full bg-[#2bcaca] text-white hover:bg-[#229999]">
                    <Heart className="mr-2 h-4 w-4" />
                    Dodaj do ulubionych
                  </Button>
                  <Button className="w-full" variant="outline">
                    <MapPin className="mr-2 h-4 w-4" />
                    Zobacz uczelnie
                  </Button>
                  <Button className="w-full" variant="outline">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Program studiów
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Kierunki studiów</h1>
          <p className="text-gray-600">
            Znajdź kierunek studiów idealnie dopasowany do Twoich zainteresowań
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  className="pl-10 border-gray-200"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Szukaj kierunku..."
                  value={searchTerm}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  className={cn(
                    selectedCategory === category
                      ? 'bg-[#2bcaca] text-white hover:bg-[#229999]'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  size="sm"
                  variant={selectedCategory === category ? 'default' : 'outline'}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredFields.map((field: StudyField) => (
            <div
              className="cursor-pointer border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md flex flex-col h-full"
              key={field.id}
              onClick={() => setSelectedField(field)}
            >
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex items-start">
                  <field.icon className="mt-1 mr-3 h-6 w-6 text-[#2bcaca]" />
                  <div className="flex-1">
                    <h3 className="mb-1 font-semibold text-gray-900">{field.name}</h3>
                    <p className="text-sm text-gray-500">{field.category}</p>
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-lg font-bold text-[#2bcaca]">
                    {field.matchPercentage}%
                  </div>
                  <p className="text-xs text-gray-500">dopasowanie</p>
                </div>
              </div>

              {/* Description */}
              <p className="mb-4 line-clamp-3 text-sm text-gray-700">
                {field.description}
              </p>

              {/* Stats */}
              <div className="mb-4 space-y-2 flex-grow">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Zatrudnialność:</span>
                  <span className="font-medium text-[#2bcaca]">
                    {field.employmentRate}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Trudność:</span>
                  <Badge
                    className={getDifficultyColor(field.difficulty)}
                    size="sm"
                    variant="outline"
                  >
                    {field.difficulty}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Czas trwania:</span>
                  <span className="font-medium">{field.duration}</span>
                </div>
              </div>

              {/* Action */}
              <Button className="w-full mt-auto" size="sm" variant="outline">
                <BookOpen className="mr-2 h-4 w-4" />
                Zobacz szczegóły
              </Button>
            </div>
          ))}
        </div>

        {filteredFields.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-gray-500">
              Nie znaleziono kierunków spełniających kryteria wyszukiwania.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
